import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Button, Popconfirm, message } from "antd";
import { DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable, { DataColumn } from "../DataTable";
import { SearchInput } from "../Filters";
import {
  DELETE_IDFA_REPORT_FILE,
  GET_ORDER_IDFA_REPORT,
} from "../../api/graphql/idfaReports";
import {
  IdfaExportFormat,
  IdfaImportResult,
  downloadIdfaReport,
} from "../../api/idfaReports";
import ImportResults from "./ImportResults";
import styles from "./styles.module.scss";

type ReportRow = {
  id: number;
  idfa: string;
  completedAt: string;
  keyword: string | null;
};

type ReportFile = {
  id: number;
  fileName: string;
  sourcePath: string;
  contractorTaskId: string | null;
  country: string | null;
  rowsCount: number;
  createdAt: string;
};

/** Saves the order's IDFAs; shared by the order page and the reports list. */
export const ExportButtons: React.FC<{
  orderType: string;
  orderId: number;
  disabled?: boolean;
  size?: "small" | "middle";
}> = ({ orderType, orderId, disabled, size = "middle" }) => {
  const [busy, setBusy] = useState<IdfaExportFormat | null>(null);
  const download = async (format: IdfaExportFormat) => {
    setBusy(format);
    try {
      await downloadIdfaReport(orderType, orderId, format);
    } catch (error: any) {
      message.error(error?.message || "The report was not downloaded");
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      {(["csv", "xlsx"] as IdfaExportFormat[]).map((format) => (
        <Button
          key={format}
          size={size}
          // In a table row the format alone says what the button saves.
          icon={size === "small" ? undefined : <DownloadOutlined />}
          loading={busy === format}
          disabled={disabled || (busy !== null && busy !== format)}
          onClick={() => download(format)}
        >
          {format.toUpperCase()}
        </Button>
      ))}
    </>
  );
};

interface OrderIdfaReportProps {
  orderType: string;
  orderId: number;
  /** The last upload from this page: its result is shown, the rows reload. */
  lastImport: IdfaImportResult | null;
}

/**
 * The IDFAs the contractor sent for one order, in the client's timezone,
 * with the files they came from.
 */
const OrderIdfaReport: React.FC<OrderIdfaReportProps> = ({
  orderType,
  orderId,
  lastImport,
}) => {
  const [search, setSearch] = useState("");
  const { data, loading, error, refetch } = useQuery(GET_ORDER_IDFA_REPORT, {
    variables: { orderType, orderId },
    fetchPolicy: "cache-and-network",
  });
  const [deleteFile] = useMutation(DELETE_IDFA_REPORT_FILE);

  useEffect(() => {
    if (lastImport) refetch();
  }, [lastImport, refetch]);

  const report = data?.getOrderIdfaReport;
  const rows: ReportRow[] = useMemo(() => report?.rows ?? [], [report?.rows]);
  const files: ReportFile[] = report?.files ?? [];
  const timezone = report?.timezone ?? "UTC";

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (row) =>
        row.idfa.toLowerCase().includes(query) ||
        (row.keyword || "").toLowerCase().includes(query),
    );
  }, [rows, search]);

  const rowColumns: DataColumn<ReportRow>[] = [
    {
      key: "idfa",
      title: "IDFA",
      width: 340,
      fixed: true,
      render: (row) => row.idfa,
      sorter: (a, b) => a.idfa.localeCompare(b.idfa),
    },
    {
      key: "completedAt",
      title: `Completed at (${timezone})`,
      width: 240,
      mobile: "full",
      render: (row) => row.completedAt,
      sorter: (a, b) => a.completedAt.localeCompare(b.completedAt),
    },
    {
      key: "keyword",
      title: "Keyword",
      width: 240,
      mobile: "full",
      render: (row) => row.keyword || "—",
      sorter: (a, b) => (a.keyword || "").localeCompare(b.keyword || ""),
    },
  ];

  const removeFile = async (file: ReportFile) => {
    try {
      await deleteFile({ variables: { fileId: file.id } });
      message.success(`${file.fileName} removed`);
      refetch();
    } catch (e: any) {
      message.error(e?.message || "The file was not removed");
    }
  };

  const fileColumns: DataColumn<ReportFile>[] = [
    {
      key: "fileName",
      title: "File",
      width: 320,
      fixed: true,
      render: (file) => <span title={file.sourcePath}>{file.fileName}</span>,
      sorter: (a, b) => a.fileName.localeCompare(b.fileName),
    },
    {
      key: "country",
      title: "Country",
      width: 150,
      render: (file) => file.country || "—",
    },
    {
      key: "rowsCount",
      title: "Rows",
      width: 96,
      render: (file) => file.rowsCount.toLocaleString("en-US"),
      sorter: (a, b) => a.rowsCount - b.rowsCount,
    },
    {
      key: "createdAt",
      title: "Imported",
      width: 150,
      render: (file) => dayjs(file.createdAt).format("DD.MM.YYYY HH:mm"),
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
    {
      key: "actions",
      title: "",
      width: 72,
      mobile: "header",
      align: "center",
      render: (file) => (
        <Popconfirm
          title="Remove this file's IDFAs?"
          description="Upload it again to bring them back."
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={() => removeFile(file)}
        >
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            aria-label={`Remove ${file.fileName}`}
          />
        </Popconfirm>
      ),
    },
  ];

  const hasRows = rows.length > 0;

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.heading}>
          <h2 className={styles.sectionTitle}>IDFA report</h2>
          <p className={styles.meta}>
            {hasRows
              ? `${rows.length.toLocaleString("en-US")} IDFAs, ${(
                  report?.uniqueIdfas ?? 0
                ).toLocaleString("en-US")} unique, from ${files.length} file${
                  files.length === 1 ? "" : "s"
                } · times in ${timezone}, the client's timezone`
              : "The contractor's IDFAs for this order appear here after Upload ID."}
          </p>
        </div>
        <div className={styles.actions}>
          {hasRows && (
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search IDFA or keyword"
            />
          )}
          <ExportButtons
            orderType={orderType}
            orderId={orderId}
            disabled={!hasRows}
          />
        </div>
      </div>

      {lastImport && (
        <ImportResults result={lastImport} id="idfa-order-import" />
      )}

      {error ? (
        <p className={styles.meta}>
          The IDFA report did not load: {error.message}
        </p>
      ) : loading && !report ? (
        <p className={styles.meta}>Loading…</p>
      ) : (
        hasRows && (
          <>
            <div className={styles.tableBox}>
              <DataTable
                id="idfa-report-rows"
                columns={rowColumns}
                rows={visibleRows}
                rowKey={(row) => row.id}
                defaultSort={{ key: "completedAt", direction: "asc" }}
                emptyText="Nothing matches the search"
                resetKey={search}
              />
            </div>
            <details className={styles.files}>
              <summary>Files ({files.length})</summary>
              <div className={styles.tableBox}>
                <DataTable
                  id="idfa-report-files"
                  columns={fileColumns}
                  rows={files}
                  rowKey={(file) => file.id}
                />
              </div>
            </details>
          </>
        )
      )}
    </section>
  );
};

export default OrderIdfaReport;
